/**
 * 3-column layout for Scale/Edit/BulkEdit modals.
 * Config (left) | Creative (center) | Leads (right)
 * Matches the creator-style horizontal flow.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useState, type FC, type ReactNode } from 'react';
import { X } from 'lucide-react';

import { Button, ModalPortalSurfaceContext } from '@/components/ui';
import { cn } from '@/lib/utils';
import { backdropClick } from '@/components/ui';

export interface ScaleDropModalLayoutProps {
  title: string;
  icon?: ReactNode;
  onClose: () => void;
  isBusy: boolean;
  loadingOverlay?: ReactNode;
  footer: ReactNode;
  /** Optional actions in header (e.g. Paste copied config) */
  headerActions?: ReactNode;
  /** Centered hint in header: same row as title from md up; stacked centered on small screens */
  headerSubtitle?: ReactNode;
  /** Left column: configuration + drop options (fixed width, no internal scroll needed) */
  configSection: ReactNode;
  /** Center column: senders + texts side-by-side (flexible, scrollable) */
  creativeSection: ReactNode;
  /** Right column: leads list picker (fixed max-width, scrollable) */
  leadsSection: ReactNode;
}

export const ScaleDropModalLayout: FC<ScaleDropModalLayoutProps> = ({
  title,
  icon,
  onClose,
  isBusy,
  loadingOverlay,
  footer,
  headerActions,
  headerSubtitle,
  configSection,
  creativeSection,
  leadsSection,
}) => {
  const [portalSurface, setPortalSurface] = useState<HTMLDivElement | null>(null);
  const surfaceRef = useCallback((node: HTMLDivElement | null) => {
    setPortalSurface(node);
  }, []);

  const rightHeader = (
    <>
      {headerActions}
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} disabled={isBusy} aria-label="Close">
        <X className="h-4 w-4" />
      </Button>
    </>
  );

  return (
    <ModalPortalSurfaceContext.Provider value={portalSurface}>
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm"
        onClick={backdropClick(onClose)}
      >
        <div
          ref={surfaceRef}
          className="relative flex max-h-[94vh] w-[96vw] max-w-[1600px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 border-b border-border bg-muted/30 px-5 py-3">
            {headerSubtitle ? (
              <div
                className={cn(
                  'grid grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] gap-x-3 gap-y-2',
                  'md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:grid-rows-1 md:items-center md:gap-y-0',
                )}
              >
                <h3 className="col-start-1 row-start-1 flex min-w-0 items-center gap-2 text-base font-semibold tracking-tight md:justify-self-start">
                  {icon} {title}
                </h3>
                <div className="col-start-2 row-start-1 flex shrink-0 items-center justify-end gap-2 md:col-start-3 md:justify-self-end">
                  {rightHeader}
                </div>
                <p className="col-span-2 row-start-2 text-center text-xs leading-snug text-muted-foreground md:col-span-1 md:col-start-2 md:row-start-1 md:max-w-lg md:justify-self-center md:px-2">
                  {headerSubtitle}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <h3 className="flex min-w-0 items-center gap-2 text-base font-semibold tracking-tight">
                  {icon} {title}
                </h3>
                <div className="flex shrink-0 items-center gap-2">{rightHeader}</div>
              </div>
            )}
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto xl:flex-row xl:overflow-y-hidden">
            {/* 320px: at 300px the config selects clipped their own labels. */}
            <div className="flex w-full shrink-0 flex-col border-b border-border overflow-x-hidden xl:w-[320px] xl:min-w-0 xl:border-b-0 xl:border-r">
              {configSection}
            </div>
            <div className="flex min-h-[320px] min-w-0 flex-1 flex-col border-b border-border xl:min-h-0 xl:border-b-0 xl:border-r">
              {creativeSection}
            </div>
            <div className="flex min-h-[280px] min-w-0 shrink-0 flex-col overflow-y-auto overflow-x-hidden xl:w-[420px] xl:max-w-[480px]">
              {leadsSection}
            </div>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-muted/20 px-5 py-3">
            {footer}
          </div>
          {loadingOverlay}
        </div>
      </div>
    </ModalPortalSurfaceContext.Provider>
  );
};
